/**
 * Utilidad para limpiar y normalizar datos de solicitud antes de enviar al backend
 * Previene errores 422 por validaciones de Pydantic
 */

/**
 * Limpia datos de solicitud eliminando campos con strings vacíos o muy cortos
 * y normalizando campos que deben ser listas
 * 
 * @param {Object} solicitud - Objeto de solicitud a limpiar
 * @returns {Object} - Objeto de solicitud limpio
 */
export const limpiarDatosSolicitud = (solicitud) => {
  const limpio = { ...solicitud };
  
  // Campos con min_length que deben eliminarse si están vacíos o muy cortos
  const camposConMinLength = [
    'titular',
    'solicitante',
    'detalle',
    'diagnostico',
    'recibe',
    'direccion',
    'informe',
    'recipe',
    'subdiagnosticoFarmacologico',
    'codigoSubdiagnosticoFarmacologico'
  ];
  
  // Eliminar campos con strings vacíos o menores a 3 caracteres
  camposConMinLength.forEach(campo => {
    if (limpio[campo] !== undefined && 
        (limpio[campo] === '' || 
         limpio[campo] === null || 
         (typeof limpio[campo] === 'string' && limpio[campo].trim().length < 3))) {
      delete limpio[campo];
    }
  });
  
  // Normalizar campos que deben ser listas
  // Si no son arrays válidos, convertirlos a arrays vacíos
  const camposLista = [
    'medicamentos',
    'facturacion',
    'delivery',
    'comentarios',
    'calificaciones',
    'adjuntos'
  ];
  
  camposLista.forEach(campo => {
    if (limpio[campo] !== undefined && !Array.isArray(limpio[campo])) {
      limpio[campo] = [];
    }
  });
  
  // Normalizar campo cronico si existe
  if (limpio.cronico !== undefined && limpio.cronico !== null) {
    // Asegurar que historialEntregas sea una lista
    if (limpio.cronico.historialEntregas !== undefined && 
        !Array.isArray(limpio.cronico.historialEntregas)) {
      limpio.cronico.historialEntregas = [];
    }
    
    // Asegurar que ciclos sea una lista
    if (limpio.cronico.ciclos !== undefined && 
        !Array.isArray(limpio.cronico.ciclos)) {
      limpio.cronico.ciclos = [];
    }
  }
  
  return limpio;
};

/**
 * Wrapper para dispatch de editSolicitud que limpia los datos automáticamente
 * 
 * @param {Function} dispatch - Función dispatch de Redux
 * @param {Function} editSolicitud - Action creator de editSolicitud
 * @param {Object} solicitud - Objeto de solicitud a editar
 * @returns {Promise} - Promesa del dispatch
 */
export const dispatchEditSolicitudLimpio = async (dispatch, editSolicitud, solicitud) => {
  const solicitudLimpia = limpiarDatosSolicitud(solicitud);
  return await dispatch(editSolicitud(solicitudLimpia));
};

/**
 * Extrae el valor numérico del monto de cobertura
 * Maneja diferentes formatos: objeto con parsedValue (Latina Salud) o número directo (MyH+)
 * 
 * @param {Object|number} montoCobertura - Monto de cobertura (objeto o número)
 * @returns {number} - Valor numérico del monto de cobertura
 */
export const getMontoCobertura = (montoCobertura) => {
  if (!montoCobertura) return 0;
  
  // Latina Salud: objeto con parsedValue
  if (typeof montoCobertura === 'object' && montoCobertura !== null) {
    return montoCobertura.parsedValue || 0;
  }
  
  // MyH+ y otros: número directo
  return typeof montoCobertura === 'number' ? montoCobertura : 0;
};
